import { canvasCoordsFromEvent, pointerKind as detectPointerKind, type PointerKind as PkgPointerKind } from '@bn-games/input-core';
import type { Square } from '../game/types.js';
import { canvasToSquare, type ViewerRotation } from '../render/geometry.js';
import { CANVAS_SIZE } from '../utils/constants.js';

export type InteractionKind = 'down' | 'move' | 'up' | 'leave' | 'cancel';
export type PointerKind = PkgPointerKind;

/**
 * Normalized pointer event in canvas-drawing coordinates (CSS pixels,
 * matching `ctx.scale(dpr, dpr)`). Both raw canvas coords and the mapped
 * board square (or `null` if off-board) are provided so callers can pick
 * whichever they need without re-implementing the geometry.
 */
export interface PointerInteraction {
  readonly kind: InteractionKind;
  readonly canvasX: number;
  readonly canvasY: number;
  readonly square: Square | null;
  readonly pointer: PointerKind;
}

export interface InputHandlerOptions {
  readonly canvas: HTMLCanvasElement;
  readonly getRotation: () => ViewerRotation;
  readonly onInteraction: (event: PointerInteraction) => void;
}

/**
 * Wraps the canvas in Pointer Events so the rest of the input pipeline can
 * stay agnostic of mouse vs. touch. Coords are mapped to drawing space (CSS
 * pixels) so they line up with `squareToCanvas*` outputs even when the canvas
 * is stretched to viewport on mobile.
 */
export class InputHandler {
  private readonly disposers: Array<() => void> = [];

  constructor(private readonly opts: InputHandlerOptions) {
    this.attach();
  }

  private attach(): void {
    const { canvas } = this.opts;

    const dispatch = (kind: InteractionKind, e: PointerEvent): void => {
      if (!e.isPrimary) return;
      const { x, y } = canvasCoordsFromEvent(e, canvas, { width: CANVAS_SIZE, height: CANVAS_SIZE });
      const sq = canvasToSquare(x, y, this.opts.getRotation());
      this.opts.onInteraction({ kind, canvasX: x, canvasY: y, square: sq, pointer: detectPointerKind(e) });
    };

    const onDown = (e: PointerEvent): void => {
      dispatch('down', e);
      try {
        canvas.setPointerCapture(e.pointerId);
      } catch {
        // Some platforms throw if pointer already captured; safe to ignore.
      }
    };
    const onMove = (e: PointerEvent): void => dispatch('move', e);
    const onUp = (e: PointerEvent): void => {
      dispatch('up', e);
      try {
        canvas.releasePointerCapture(e.pointerId);
      } catch {
        // Pointer may already be released; ignore.
      }
    };
    const onCancel = (e: PointerEvent): void => dispatch('cancel', e);
    const onLeave = (e: PointerEvent): void => dispatch('leave', e);
    // Avoid native drag-image preview on desktop when the user grabs a piece.
    const onDragStart = (e: DragEvent): void => e.preventDefault();

    canvas.addEventListener('pointerdown', onDown);
    canvas.addEventListener('pointermove', onMove);
    canvas.addEventListener('pointerup', onUp);
    canvas.addEventListener('pointercancel', onCancel);
    canvas.addEventListener('pointerleave', onLeave);
    canvas.addEventListener('dragstart', onDragStart);

    this.disposers.push(
      () => canvas.removeEventListener('pointerdown', onDown),
      () => canvas.removeEventListener('pointermove', onMove),
      () => canvas.removeEventListener('pointerup', onUp),
      () => canvas.removeEventListener('pointercancel', onCancel),
      () => canvas.removeEventListener('pointerleave', onLeave),
      () => canvas.removeEventListener('dragstart', onDragStart),
    );
  }

  dispose(): void {
    for (const fn of this.disposers) fn();
    this.disposers.length = 0;
  }
}
